import { z } from 'zod';
/**
 * Mifflin-St Jeor BMR + TDEE + macro split.
 *
 * Pure code (FR-5.4 — macro arithmetic excluded from the AI layer).
 * Lives in @nutians/shared so the frontend can show live previews as the
 * user edits onboarding inputs (ISSUE-011) without a server round-trip.
 *
 * References:
 *   - Mifflin MD et al. A new predictive equation for resting energy expenditure
 *     in healthy individuals. Am J Clin Nutr. 1990;51(2):241-7.
 *   - D-06 (locked: Mifflin-St Jeor only at MVP).
 */
export const biologicalSexSchema = z.enum(['male', 'female']);
export const activityLevelSchema = z.enum([
    'sedentary', // mostly desk work
    'light', // 1–3 sessions/week
    'moderate', // 3–5 sessions/week
    'active', // 6–7 sessions/week
    'very_active', // physical job + training
]);
export const goalSchema = z.enum([
    'fat_loss',
    'maintenance',
    'recomp',
    'muscle_gain',
    'performance',
]);
export const macroPrioritySchema = z.enum(['protein_first', 'satiety', 'energy']);
/** Activity factor multiplier for Mifflin-St Jeor TDEE. Standard ACSM values. */
const ACTIVITY_FACTOR = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9,
};
/** Goal-driven calorie delta vs maintenance (TDEE). Positive = surplus, negative = deficit. */
const GOAL_KCAL_DELTA = {
    fat_loss: -500, // ~0.5 kg/week
    maintenance: 0,
    recomp: -200, // small deficit, sustained
    muscle_gain: +300,
    performance: 0, // intake matches need
};
/** Protein g/kg by goal. Protein-first priority overrides for any goal except fat_loss-aggressive. */
const PROTEIN_PER_KG = {
    fat_loss: 2.2,
    maintenance: 1.8,
    recomp: 2.4,
    muscle_gain: 2.0,
    performance: 1.8,
};
/** Fat floor: minimum essential fat to support hormonal function. ACSM ~0.6 g/kg lean mass. */
const FAT_FLOOR_G_PER_KG = 0.6;
/**
 * Mifflin-St Jeor BMR (kcal/day):
 *   male:   10·W + 6.25·H − 5·A + 5
 *   female: 10·W + 6.25·H − 5·A − 161
 */
export function computeBMR(input) {
    const { sex, weightKg, heightCm, ageYears } = input;
    if (weightKg <= 0 || heightCm <= 0 || ageYears <= 0) {
        throw new Error('BMR inputs must be positive (weightKg, heightCm, ageYears).');
    }
    const base = 10 * weightKg + 6.25 * heightCm - 5 * ageYears;
    return Math.round(sex === 'male' ? base + 5 : base - 161);
}
export function computeTDEE(input) {
    const factor = ACTIVITY_FACTOR[input.activityLevel];
    return Math.round(input.bmr * factor);
}
const KCAL_PER_G = { protein: 4, carbs: 4, fat: 9 };
/** Fiber target: 14g per 1000 kcal (US Dietary Guidelines). */
function fiberTargetG(kcal) {
    return Math.round((kcal / 1000) * 14);
}
export function computeMacroSplit(input) {
    const { tdee, weightKg, goal, macroPriority, safetyProfile } = input;
    const notes = [];
    // 1. Start from goal-driven calorie target.
    let targetKcal = tdee + GOAL_KCAL_DELTA[goal];
    let cappedBySafety = false;
    // 2. Safety: cap aggressive deficits for ED-flagged users (ISSUE-058 / D-20).
    if (safetyProfile?.flaggedEd) {
        const minSafeKcal = Math.round(tdee * 0.75);
        if (targetKcal < minSafeKcal) {
            targetKcal = minSafeKcal;
            cappedBySafety = true;
            notes.push('Calorie target capped at 75% of maintenance (ED safety profile).');
        }
    }
    // 3. Protein floor by goal, possibly bumped by priority.
    let proteinPerKg = PROTEIN_PER_KG[goal];
    if (macroPriority === 'protein_first') {
        proteinPerKg = Math.max(proteinPerKg, 2.2);
    }
    const proteinG = Math.round(proteinPerKg * weightKg);
    const proteinKcal = proteinG * KCAL_PER_G.protein;
    // 4. Fat floor.
    const fatG = Math.max(Math.round(FAT_FLOOR_G_PER_KG * weightKg), Math.round((targetKcal * 0.2) / KCAL_PER_G.fat));
    const fatKcal = fatG * KCAL_PER_G.fat;
    // 5. Remaining kcal → carbs.
    const carbKcal = Math.max(0, targetKcal - proteinKcal - fatKcal);
    const carbsG = Math.round(carbKcal / KCAL_PER_G.carbs);
    // 6. Re-derive actual kcal to keep arithmetic auditable.
    const actualKcal = proteinG * KCAL_PER_G.protein + carbsG * KCAL_PER_G.carbs + fatG * KCAL_PER_G.fat;
    return {
        kcal: actualKcal,
        proteinG,
        carbsG,
        fatG,
        fiberG: fiberTargetG(actualKcal),
        cappedBySafety,
        notes,
    };
}
/**
 * Validate that a user-overridden calorie target is safe.
 * Returns null if valid, otherwise an error message.
 * Used by the targets API (ISSUE-013) before persisting a target row.
 */
export function validateCustomKcalTarget(customKcal, tdee, safetyProfile) {
    if (customKcal < 1000) {
        return 'Calorie target cannot be below 1000 kcal/day.';
    }
    if (safetyProfile?.flaggedEd && customKcal < tdee * 0.75) {
        return 'Calorie target cannot be more than 25% below maintenance for your safety profile.';
    }
    return null;
}
//# sourceMappingURL=index.js.map